import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils";
import { ChevronLeft } from "lucide-react";
import React from "react";

const focusInset =
  "outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:outline-offset-[-2px]";

const rowHover = "hover:bg-n-05";

export function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="pb-1 font-mono text-2xs font-medium uppercase tracking-[0.08em] text-n-50">
      {children}
    </div>
  );
}

export function Row({
  tile,
  frameless,
  dashed,
  name,
  meta,
  onOpen,
  children,
}: {
  tile: React.ReactNode;
  frameless?: boolean;
  dashed?: boolean;
  name: React.ReactNode;
  meta?: React.ReactNode;
  onOpen?: () => void;
  children?: React.ReactNode;
}) {
  const body = (
    <>
      {frameless ? (
        tile
      ) : (
        <span
          className={cn(
            "inline-flex size-9 shrink-0 items-center justify-center rounded-md border bg-n-05",
            dashed
              ? "border-dashed border-n-20 text-n-40"
              : "border-n-10 text-n-50",
          )}
        >
          {tile}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-md font-medium text-n-90">
          {name}
        </span>
        {meta ? (
          <span className="block truncate text-sm text-n-50">{meta}</span>
        ) : null}
      </span>
    </>
  );

  return (
    <div
      className={cn(
        "group flex min-h-12 items-center gap-3 rounded-md py-2 transition-colors",
        onOpen && rowHover,
      )}
    >
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-3 rounded-md text-left",
            focusInset,
          )}
        >
          {body}
        </button>
      ) : (
        body
      )}
      {children}
    </div>
  );
}

export function BackRow({
  label,
  onBack,
}: {
  label: string;
  onBack: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onBack}
      className="self-start text-n-60 hover:text-n-90"
    >
      <ChevronLeft />
      {label}
    </Button>
  );
}

export function GroupTile({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-md border border-n-10 bg-n-05 font-medium text-n-70",
        className,
      )}
    >
      {name?.[0]?.toUpperCase() ?? "?"}
    </span>
  );
}

export function DirtyField({
  label,
  initial,
  onSave,
  isSaving,
}: {
  label: string;
  initial: string;
  onSave: (value: string) => void | Promise<void>;
  isSaving?: boolean;
}) {
  const id = React.useId();
  const [saved, setSaved] = React.useState(initial);
  const [val, setVal] = React.useState(initial);
  const dirty = val !== saved;

  React.useEffect(() => {
    setSaved(initial);
    setVal(initial);
  }, [initial]);

  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium text-n-70">
        {label}
      </label>
      <div className="mt-1.5 flex items-center gap-2">
        <Input
          id={id}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          className="min-w-0 flex-1"
        />
        {dirty ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={isSaving}
            isLoading={isSaving}
            onClick={async () => {
              await onSave(val);
              setSaved(val);
            }}
          >
            Save
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** Quiet trailing action chip (design-system `chip` via token utilities). */
export const chipButtonClassName =
  "bg-n-10 text-n-70 hover:bg-n-20 hover:text-n-95";

/** Outline destructive (design-system `outline-destructive` via tokens). */
export const outlineDestructiveClassName =
  "bg-transparent text-spectrum-red shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--spectrum-red)_55%,transparent)] hover:bg-[color-mix(in_oklab,var(--spectrum-red)_10%,transparent)] hover:shadow-[inset_0_0_0_1px_var(--spectrum-red)]";
